import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GreenCertFormData,
  initialForm,
  YEAR_OPTIONS,
  HALF_YEAR_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  PROVINCE_OPTIONS,
  SUBSIDY_OPTIONS,
  TRADE_METHOD_OPTIONS,
  EXCHANGE_OPTIONS,
  GreenCertField,
} from '@/components/feature/GreenCertFieldConfig';
import { demandApi } from '@/services/api';

interface Errors {
  [key: string]: string | undefined;
}

export default function DemandPublish() {
  const [form, setForm] = useState<GreenCertFormData>({
    ...initialForm,
    direction: 'buy',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  const updateSingle = (field: GreenCertField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const updateMulti = (field: GreenCertField, value: string) => {
    setForm((prev) => {
      const arr = (prev[field] as string[]) || [];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { ...prev, [field]: next };
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};
    if (!form.year) newErrors.year = '请选择年份';
    if (form.halfYear.length === 0) newErrors.halfYear = '请选择半年';
    if (form.projectType.length === 0) newErrors.projectType = '请选择项目类型';
    if (form.channel.length === 0) newErrors.channel = '请选择通道与否';
    if (form.provinceRequirement.length === 0) newErrors.provinceRequirement = '请选择省份要求';
    if (form.subsidy.length === 0) newErrors.subsidy = '请选择补贴与否';
    if (!form.quantity || Number(form.quantity) <= 0) newErrors.quantity = '请输入有效张数';
    if (form.tradeMethod.length === 0) newErrors.tradeMethod = '请选择交易方式';
    if (form.exchange.length === 0) newErrors.exchange = '请选择交易所';
    if (!form.expiryDate) newErrors.expiryDate = '请选择信息有效期';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await demandApi.create({
        direction: form.direction,
        year: form.year,
        halfYear: form.halfYear,
        projectType: form.projectType,
        channel: form.channel,
        provinceRequirement: form.provinceRequirement,
        subsidy: form.subsidy,
        quantity: Number(form.quantity),
        price: form.price,
        tradeMethod: form.tradeMethod,
        exchange: form.exchange,
        expiryDate: form.expiryDate,
        otherInfo: form.otherInfo,
      });
      setSubmitted(true);
    } catch {
      setSubmitError('发布失败，请检查网络后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSingleChip = (field: GreenCertField, label: string, options: { value: string; label: string }[], required: boolean) => {
    const cur = ((form[field] as string[]) || [])[0];
    return (
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground-700 mb-2">
          {label} {required && <span className="text-accent-500">*</span>}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setForm((prev) => ({ ...prev, [field]: [opt.value] }));
                if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
              }}
              className={`px-3 py-2.5 rounded-md text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                cur === opt.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-background-200 text-foreground-500 hover:border-background-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {errors[field] && <p className="text-accent-600 text-xs mt-1">{errors[field]}</p>}
      </div>
    );
  };

  const renderMulti = (field: GreenCertField, label: string, options: { value: string; label: string }[], required: boolean) => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-foreground-700 mb-2">
        {label} {required && <span className="text-accent-500">*</span>}
        <span className="text-foreground-400 font-normal text-xs ml-1">可多选</span>
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {options.map((opt) => {
          const arr = (form[field] as string[]) || [];
          const selected = arr.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateMulti(field, opt.value)}
              className={`px-3 py-2.5 rounded-md text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-background-200 text-foreground-500 hover:border-background-300'
              }`}
            >
              {selected && <i className="ri-check-line mr-1"></i>}
              {opt.label}
            </button>
          );
        })}
      </div>
      {errors[field] && <p className="text-accent-600 text-xs mt-1">{errors[field]}</p>}
    </div>
  );

  if (submitted) {
    return (
      <>
        <Navbar />
        <div className="w-full bg-background-50 min-h-screen">
          <div className="max-w-2xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-4xl text-primary-600"></i>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mt-6">需求信息发布成功！</h2>
            <p className="text-foreground-500 mt-3 text-sm leading-relaxed">
              您的绿证采购需求已提交，审核通过后将公开展示。供应方可浏览并联系您。您可在个人中心管理已发布的需求信息。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <Link
                to="/demand"
                className="px-6 py-2.5 border border-background-300 text-foreground-700 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回需求列表
              </Link>
              <Link
                to="/profile"
                className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往个人中心
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Link to="/demand" className="inline-flex items-center gap-1 text-foreground-500 text-sm hover:text-foreground-700 transition-colors cursor-pointer mb-4">
              <i className="ri-arrow-left-line"></i>
              返回需求列表
            </Link>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">发布采购需求</h1>
            <p className="text-foreground-500 text-sm mt-2">填写以下信息发布您的绿证采购需求，供应方将能浏览并联系您</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
            {/* 交易方向 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">交易方向</label>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent-100 border border-accent-200 rounded-md text-accent-900 text-sm font-medium">
                <i className="ri-arrow-down-line"></i> 买入
              </div>
            </div>

            {/* 年份 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">年份 <span className="text-accent-500">*</span></label>
              <select
                value={form.year}
                onChange={(e) => updateSingle('year', e.target.value)}
                className={`w-full px-4 py-2.5 bg-background-100 border rounded-md text-sm text-foreground-700 outline-none transition-colors cursor-pointer ${
                  errors.year ? 'border-accent-400' : 'border-background-200 focus:border-primary-400'
                }`}
              >
                <option value="">请选择年份</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}年</option>
                ))}
              </select>
              {errors.year && <p className="text-accent-600 text-xs mt-1">{errors.year}</p>}
            </div>

            {/* 半年 - 多选 */}
            {renderMulti('halfYear', '半年', HALF_YEAR_OPTIONS, true)}
            {/* 项目类型 - 多选 */}
            {renderMulti('projectType', '项目类型', PROJECT_TYPE_OPTIONS, true)}
            {/* 通道与否 - 单选 */}
            {renderSingleChip('channel', '通道与否', CHANNEL_OPTIONS, true)}
            {/* 省份要求 - 多选 */}
            {renderMulti('provinceRequirement', '省份要求', PROVINCE_OPTIONS, true)}
            {/* 补贴与否 - 单选 */}
            {renderSingleChip('subsidy', '补贴与否', SUBSIDY_OPTIONS, true)}

            {/* 张数 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">张数 <span className="text-accent-500">*</span></label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => updateSingle('quantity', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="请输入预计采购数量"
                min="1"
                className={`w-full px-4 py-2.5 bg-background-100 border rounded-md text-sm text-foreground-700 outline-none transition-colors ${
                  errors.quantity ? 'border-accent-400' : 'border-background-200 focus:border-primary-400'
                }`}
              />
              {errors.quantity && <p className="text-accent-600 text-xs mt-1">{errors.quantity}</p>}
            </div>

            {/* 价格 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">价格 <span className="text-foreground-400 font-normal text-xs">选填</span></label>
              <input
                type="text"
                value={form.price}
                onChange={(e) => updateSingle('price', e.target.value)}
                placeholder="例如：30-35 元/张"
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
              />
            </div>

            {/* 交易方式 - 单选 */}
            {renderMulti('tradeMethod', '交易方式', TRADE_METHOD_OPTIONS, true)}
            {/* 交易所 - 多选 */}
            {renderMulti('exchange', '交易所', EXCHANGE_OPTIONS, true)}

            {/* 信息有效期 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">信息有效期 <span className="text-accent-500">*</span></label>
              <input
                type="date"
                value={form.expiryDate}
                onChange={(e) => updateSingle('expiryDate', e.target.value)}
                className={`w-full px-4 py-2.5 bg-background-100 border rounded-md text-sm text-foreground-700 outline-none transition-colors cursor-pointer ${
                  errors.expiryDate ? 'border-accent-400' : 'border-background-200 focus:border-primary-400'
                }`}
              />
              {errors.expiryDate && <p className="text-accent-600 text-xs mt-1">{errors.expiryDate}</p>}
            </div>

            {/* 其他信息 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">其他信息 <span className="text-foreground-400 font-normal text-xs">选填</span></label>
              <textarea
                value={form.otherInfo}
                onChange={(e) => updateSingle('otherInfo', e.target.value)}
                placeholder="请补充对绿证来源、项目类型、交付时间等具体要求..."
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors resize-none"
              />
              <p className="text-foreground-400 text-xs mt-1">{form.otherInfo.length}/500</p>
            </div>

            {submitError && (
              <div className="mb-4 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-accent-700 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {submitError}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-background-200">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {submitting ? '提交中...' : '提交发布'}
              </button>
            </div>
            <p className="text-foreground-400 text-xs mt-4 leading-relaxed">
              提示：提交即表示您同意本平台的《信息发布管理规范》。平台不对信息真实性进行核验，交易风险由双方自行承担。
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
